<script setup>
import { computed, getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  status: { type: [String, Number], default: '' },
  title: { type: String, default: '' },
  description: { type: String, default: '' },
  showDefaultDescription: { type: Boolean, default: false },
  actionLabel: { type: String, default: '' },
  showAction: { type: Boolean, default: false },
  actionAriaLabel: { type: String, default: '' },
  actionIcon: { type: String, default: 'i-lucide-refresh-cw' },
  actionDisabled: { type: Boolean, default: false },
  actionLoading: { type: Boolean, default: false },
  icon: { type: String, default: 'i-lucide-house-x' },
});

const emit = defineEmits(['action']);

const { t } = useI18n();
const instance = getCurrentInstance();
const baseId = `lais-error-state-${instance?.uid || 'default'}`;
const titleId = `${baseId}-title`;
const statusId = `${baseId}-status`;
const descriptionId = `${baseId}-description`;

const hasStatus = computed(
  () =>
    props.status !== null &&
    props.status !== undefined &&
    String(props.status).length > 0
);

const statusLabel = computed(() =>
  hasStatus.value ? t('LAIS_ERROR_STATE.STATUS', { status: props.status }) : ''
);

const resolvedTitle = computed(
  () => props.title || t('LAIS_ERROR_STATE.TITLE')
);

const resolvedDescription = computed(() => {
  if (props.description) return props.description;
  return props.showDefaultDescription ? t('LAIS_ERROR_STATE.DESCRIPTION') : '';
});

const resolvedActionLabel = computed(() => {
  if (props.actionLabel) return props.actionLabel;
  return props.showAction ? t('LAIS_ERROR_STATE.ACTION') : '';
});

const actionAccessibleLabel = computed(
  () => props.actionAriaLabel || resolvedActionLabel.value
);

const describedBy = computed(() => {
  const ids = [];
  if (hasStatus.value) ids.push(statusId);
  if (resolvedDescription.value) ids.push(descriptionId);
  return ids.length ? ids.join(' ') : undefined;
});

const isActionDisabled = computed(
  () => props.actionDisabled || props.actionLoading
);

const handleAction = event => {
  if (isActionDisabled.value) {
    event.preventDefault();
    return;
  }

  emit('action', event);
};
</script>

<template>
  <section
    role="alert"
    :aria-labelledby="titleId"
    :aria-describedby="describedBy"
    class="grid min-h-[420px] w-full items-center gap-6 rounded-lais bg-lais-bg-card px-4 py-8 text-lais-text shadow-lais-soft sm:px-6 md:grid-cols-[auto_minmax(0,1fr)] md:gap-8 md:px-8 md:py-10"
  >
    <div
      class="mx-auto grid size-36 place-items-center rounded-lais-circle border border-lais-border bg-lais-danger-50 text-lais-danger shadow-lais-soft sm:size-44 md:size-56"
    >
      <slot name="icon">
        <span class="size-16 sm:size-20" :class="icon" aria-hidden="true" />
      </slot>
    </div>
    <div class="min-w-0 text-center md:text-left">
      <p
        v-if="hasStatus"
        :id="statusId"
        class="text-lais-label-sm font-semibold uppercase text-lais-text-body"
      >
        {{ statusLabel }}
      </p>
      <h1
        :id="titleId"
        class="mt-2 text-pretty font-redHatDisplay text-lais-heading-lg font-semibold text-lais-text"
      >
        {{ resolvedTitle }}
      </h1>
      <p
        v-if="resolvedDescription"
        :id="descriptionId"
        class="mt-3 max-w-[42rem] text-lais-body-md text-lais-text-body md:mx-0"
      >
        {{ resolvedDescription }}
      </p>
      <button
        v-if="resolvedActionLabel"
        type="button"
        class="mt-5 inline-flex min-h-10 items-center justify-center gap-2 rounded-lais bg-lais-primary px-4 py-2.5 font-redHatText text-lais-body-md font-semibold text-white shadow-lais-soft transition-colors hover:bg-lais-primary-600 focus-visible:outline-none focus-visible:shadow-lais-focus disabled:cursor-not-allowed disabled:bg-lais-primary-600 disabled:text-white [.lais-dark_&]:text-lais-primary-deep"
        :aria-label="actionAccessibleLabel"
        :aria-busy="actionLoading ? 'true' : undefined"
        :disabled="isActionDisabled"
        @click="handleAction"
      >
        <span
          v-if="actionLoading"
          class="i-lucide-loader-circle size-4 shrink-0 animate-spin"
          aria-hidden="true"
        />
        <span
          v-else-if="actionIcon"
          class="size-4 shrink-0"
          :class="actionIcon"
          aria-hidden="true"
        />
        <span>{{ resolvedActionLabel }}</span>
      </button>
      <slot />
    </div>
  </section>
</template>
